import { useState } from 'react';
import { Confirm, ErrorState, Field, Spinner } from '../../components/ui.jsx';
import { api, ApiError } from '../../api/client.js';
import { useFetch, useTitle } from '../../lib/hooks.js';
import { useToast } from '../../state/ToastContext.jsx';

const KINDS = [
  { slug: 'mtn_momo', label: 'MTN Mobile Money' },
  { slug: 'orange_money', label: 'Orange Money' },
  { slug: 'bank_transfer', label: 'Bank transfer' },
  { slug: 'other', label: 'Other' }
];

const emptyForm = { kind: 'mtn_momo', label: '', accountName: '', accountNumber: '', bankName: '' };

const AccountForm = ({ initial, onSaved, onCancel }) => {
  const [form, setForm] = useState(initial || emptyForm);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  const set = (patch) => setForm((current) => ({ ...current, ...patch }));
  const editing = Boolean(initial?.id);

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setErrors({});
    try {
      const payload = {
        kind: form.kind,
        label: form.label.trim() || undefined,
        accountName: form.accountName.trim(),
        accountNumber: form.accountNumber.trim(),
        bankName: form.kind === 'bank_transfer' ? form.bankName.trim() : undefined
      };
      if (editing) {
        await api.patch(`/landlord/payment-accounts/${initial.id}`, payload);
      } else {
        await api.post('/landlord/payment-accounts', payload);
      }
      toast.success(editing ? 'Saved.' : 'Account added.');
      onSaved();
    } catch (error) {
      if (error instanceof ApiError && error.details) setErrors(error.details);
      else toast.error(error.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="stack card" onSubmit={submit}>
      <div className="grid-2">
        <Field label="Type" error={errors.kind} required>
          <select value={form.kind} onChange={(event) => set({ kind: event.target.value })}>
            {KINDS.map((kind) => (
              <option key={kind.slug} value={kind.slug}>
                {kind.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Label" hint="Optional, e.g. 'Main account'" error={errors.label}>
          <input type="text" value={form.label} onChange={(event) => set({ label: event.target.value })} />
        </Field>
      </div>

      <div className="grid-2">
        <Field label="Name on the account" error={errors.accountName} required>
          <input
            type="text"
            value={form.accountName}
            onChange={(event) => set({ accountName: event.target.value })}
            required
          />
        </Field>
        <Field
          label={form.kind === 'bank_transfer' ? 'Account number' : 'Phone number'}
          error={errors.accountNumber}
          required
        >
          <input
            type="text"
            value={form.accountNumber}
            onChange={(event) => set({ accountNumber: event.target.value })}
            placeholder={form.kind === 'bank_transfer' ? '' : '+237 6 70 00 00 00'}
            required
          />
        </Field>
      </div>

      {form.kind === 'bank_transfer' ? (
        <Field label="Bank name" error={errors.bankName} required>
          <input type="text" value={form.bankName} onChange={(event) => set({ bankName: event.target.value })} required />
        </Field>
      ) : null}

      <div className="row-actions">
        <button type="button" className="btn btn-ghost" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <button type="submit" className="btn btn-primary" disabled={busy}>
          {busy ? 'Saving...' : editing ? 'Save changes' : 'Add account'}
        </button>
      </div>
    </form>
  );
};

const PaymentAccounts = () => {
  useTitle('Payment accounts');
  const toast = useToast();
  const { data, loading, error, reload } = useFetch('/landlord/payment-accounts');

  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [busy, setBusy] = useState(false);

  const setPrimary = async (account) => {
    try {
      await api.post(`/landlord/payment-accounts/${account.id}/primary`);
      toast.success('Set as the account tenants see first.');
      reload();
    } catch (requestError) {
      toast.error(requestError.message);
    }
  };

  const remove = async () => {
    if (!deleting) return;
    setBusy(true);
    try {
      await api.del(`/landlord/payment-accounts/${deleting.id}`);
      toast.success('Removed.');
      setDeleting(null);
      reload();
    } catch (requestError) {
      toast.error(requestError.message);
    } finally {
      setBusy(false);
    }
  };

  if (loading && !data) return <Spinner />;
  if (error) {
    return (
      <div className="container section">
        <ErrorState message={error} onRetry={reload} />
      </div>
    );
  }

  const items = data.items || [];
  const kindLabel = (kind) => KINDS.find((item) => item.slug === kind)?.label || kind;

  return (
    <div className="container section">
      <header className="page-head">
        <div>
          <h1>Payment accounts</h1>
          <p className="muted">
            Where tenants send rent. Every tenant with an active lease sees these on their rent page.
          </p>
        </div>
        {!adding ? (
          <button type="button" className="btn btn-primary" onClick={() => setAdding(true)}>
            Add an account
          </button>
        ) : null}
      </header>

      {adding ? (
        <AccountForm
          onCancel={() => setAdding(false)}
          onSaved={() => {
            setAdding(false);
            reload();
          }}
        />
      ) : null}

      {items.length === 0 && !adding ? (
        <div className="notice notice-warn">
          You have not added a payment account yet. Tenants will not see anywhere to send rent until you add one.
        </div>
      ) : null}

      <ul className="payout-account-cards">
        {items.map((account) =>
          editingId === account.id ? (
            <li key={account.id}>
              <AccountForm
                initial={account}
                onCancel={() => setEditingId(null)}
                onSaved={() => {
                  setEditingId(null);
                  reload();
                }}
              />
            </li>
          ) : (
            <li key={account.id} className="card">
              <div className="cell-stack">
                <strong>
                  {kindLabel(account.kind)}
                  {account.label ? ` - ${account.label}` : ''}
                  {account.isPrimary ? ' (shown first)' : ''}
                </strong>
                <span className="muted">
                  {account.accountName} - {account.accountNumber}
                  {account.bankName ? `, ${account.bankName}` : ''}
                </span>
              </div>
              <div className="row-actions">
                {!account.isPrimary ? (
                  <button type="button" className="btn btn-outline btn-sm" onClick={() => setPrimary(account)}>
                    Show first
                  </button>
                ) : null}
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => setEditingId(account.id)}>
                  Edit
                </button>
                <button type="button" className="link-btn link-btn-danger" onClick={() => setDeleting(account)}>
                  Remove
                </button>
              </div>
            </li>
          )
        )}
      </ul>

      {deleting ? (
        <Confirm
          title="Remove this account?"
          message={`Tenants will no longer be shown ${deleting.accountName} - ${deleting.accountNumber}.`}
          confirmLabel="Remove"
          tone="danger"
          busy={busy}
          onConfirm={remove}
          onClose={() => setDeleting(null)}
        />
      ) : null}
    </div>
  );
};

export default PaymentAccounts;
